import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-dropdown-selection.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { MarktAPI } from '@chancenwerk/markt/api/MarktAPI.js';
import { MarktAkquiseContactRecord } from '@chancenwerk/markt/entities/MarktAkquiseContactRecord.js';
import { marktAkquiseStatusColor, marktAkquiseStatusText } from '@chancenwerk/markt/entities/MarktAkquiseStatus.js';
import { MarktVeranstaltungsAkquise } from '@chancenwerk/markt/entities/MarktVeranstaltungsAkquise.js';
import { MarktVeranstaltungsAkquiseEntry } from '@chancenwerk/markt/entities/MarktVeranstaltungsAkquiseEntry.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { of, switchMap, tap } from 'rxjs';
import { MarktDialogVeranstaltungEntryEdit } from '../components/dialogs/markt-dialog-veranstaltung-entry-edit.js';

/**
 * @customElement markt-page-veranstaltung-akquise
 */
@customElement('markt-page-veranstaltung-akquise')
export class MarktPageAkquise extends CrPage {
  @property() marktAkquiseID = new RXController(this, '');

  @state() _data = new RXController(
    this,
    this.marktAkquiseID.observable.pipe(
      switchMap(async id => {
        if (!id || id === '') return of(null);

        await MarktAPI.ensureMarktAkquise(id)();
        return MarktAPI.subscribeVeranstaltungsMarktAkquise(id)({
          ...MarktVeranstaltungsAkquise.allFields,
          entries: {
            ...MarktVeranstaltungsAkquiseEntry.allFields,
            contacts: {
              ...MarktAkquiseContactRecord.allFields,
              from: { _id: 1, vorname: 1, nachname: 1, email: 1 },
              to: { _id: 1, vorname: 1, nachname: 1, email: 1 },
            },
          },
        });
      }),
      switchMap(obs => obs),
      tap(val => {
        if (!val) {
          return;
        }

        this.label = `${val.partner?.kuerzel ?? ''}: ${val.partner?.name ?? ''}`;
        this.description = `Veranstaltungs-Akquise`;
      }),
    ),
  );

  @state() _selectedEntry = new RXController(this, null as Maybe<MarktVeranstaltungsAkquiseEntry>);

  constructor() {
    super();

    this.label = 'Veranstaltungs-Akquise';
    this.description = '';
    this.permission = ChancenPermission.markt.LOGIN_MARKT;
    this.backRoute = '/';
  }

  override header() {
    return html`<chancen-component-context-menu
      .items=${[
        {
          icon: 'add',
          label: 'Hinzufügen',
          action: async () => {
            const res = await MarktDialogVeranstaltungEntryEdit.showPopup<Maybe<MarktVeranstaltungsAkquiseEntry>>({
              props: {},
            });
            if (res) {
              if (!this._data.value) {
                return;
              }
              this._data.value.entries ??= [];
              this._data.value.entries.push(res);
              await this._data.value.save();
            }
          },
        },
      ]}
    ></chancen-component-context-menu>`;
  }

  private _dataView() {
    return html`
      <cr-data-view
        .options=${{
          class: MarktVeranstaltungsAkquiseEntry,
          operation: (skip, limit) => {
            return () => {
              if (skip > 0) {
                return of([]);
              }
              return of(this._data.value?.entries ?? []);
            };
          },
          isEntryClickable: true,
          onlyShowLastTranslation: true,
          customColumns: [
            { fieldName: 'semester', columnName: 'Semester' },
            { fieldName: 'name', columnName: 'Name' },
            { fieldName: 'days', columnName: 'Days', getFieldMapping: entity => (entity.days ?? []).join(', ') },
            {
              customID: 'zeitraum',
              columnName: 'Zeitraum',
              getFieldMapping: entity => {
                return `${entity.start?.toFormat('dd.MM.yyyy') ?? ''} - ${entity.end?.toFormat('dd.MM.yyyy') ?? ''}`;
              },
            },
            { fieldName: 'room', columnName: 'Raum' },
            {
              customID: 'time',
              columnName: 'Uhrzeit',
              getFieldMapping: entity => {
                return `${entity.startTime?.toFormat('HH:mm') ?? ''} - ${entity.endTime?.toFormat('HH:mm') ?? ''}`;
              },
            },
            {
              customID: 'count-contacts',
              columnName: 'Anzahl Kontakte',
              getFieldMapping: entity => {
                return `${entity.contacts?.length ?? 0}`;
              },
            },
            {
              customID: 'status',
              columnName: 'Status',
              selectionSet: { contacts: { status: 1 } },
              getFieldMapping: entity => {
                return html`<cr-text
                  bold
                  ${css({
                    padding: this.spacing.sm,
                    backgroundColor: marktAkquiseStatusColor(entity.getLatestStatus()),
                    color: 'white',
                    borderRadius: this.sizes.borderRadiusSecondary,
                    textAlign: 'center',
                  })}
                >
                  ${marktAkquiseStatusText(entity.getLatestStatus())}
                </cr-text>`;
              },
            },
            {
              customID: 'edit',
              columnName: '',
              columnWidth: 'max-content',
              getFieldMapping: entry => {
                return html`<cr-flex padding="xs" horizontal space="md">
                  <cr-button
                    mode="icon"
                    icon="edit"
                    @click=${async e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const res = await MarktDialogVeranstaltungEntryEdit.showPopup<
                        Maybe<MarktVeranstaltungsAkquiseEntry>
                      >({ props: { entry } });

                      if (res) {
                        await this._data.value?.save();
                      }
                    }}
                  ></cr-button>
                  <cr-button
                    mode="icon"
                    tone="error"
                    icon="delete"
                    @click=${async e => {
                      e.preventDefault();
                      e.stopPropagation();
                      this._data.value?.entries?.splice(this._data.value.entries.indexOf(entry), 1);
                      await this._data.value?.save();
                    }}
                  ></cr-button>
                </cr-flex>`;
              },
            },
          ],
        } as DataViewOptions<typeof MarktVeranstaltungsAkquiseEntry>}
        @entry-clicked=${(e: CustomEvent<{ entity: MarktVeranstaltungsAkquiseEntry }>) => {
          this._selectedEntry.next(e.detail.entity);
        }}
      ></cr-data-view>
    `;
  }

  private _entryInfo(key: string, value: string) {
    return html`<cr-flex space="md" crossaxis-center>
      <cr-text bold>${key}</cr-text>
      <cr-text ${css({ fontSize: '1.2rem' })}>${value}</cr-text>
    </cr-flex>`;
  }

  private _entry() {
    if (!this._selectedEntry.value) {
      return html``;
    }

    this._selectedEntry.value.contacts ??= [];

    return html`<cr-card padding="md" space="md">
      <chancen-component-card-title> Kontakte für ${this._selectedEntry.value.name} </chancen-component-card-title>

      <cr-flex
        horizontal
        padding="lg"
        space="xxl"
        space-between
        ${css({ backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: this.sizes.borderRadiusSecondary })}
      >
        ${this._entryInfo('Name', this._selectedEntry.value.name ?? '')}
        ${this._entryInfo('Semester', this._selectedEntry.value.semester ?? '')}
        ${this._entryInfo('Raum', this._selectedEntry.value.room ?? '')}
        ${this._entryInfo('Wochentage', (this._selectedEntry.value.days ?? []).join(', '))}
        ${this._entryInfo(
          'Zeitraum',
          `${this._selectedEntry.value.start?.toFormat('dd.MM.yyyy') ?? ''} - ${
            this._selectedEntry.value.end?.toFormat('dd.MM.yyyy') ?? ''
          }`,
        )}
        ${this._entryInfo(
          'Uhrzeit',
          `${this._selectedEntry.value.startTime?.toFormat('HH:mm') ?? ''} - ${
            this._selectedEntry.value.endTime?.toFormat('HH:mm') ?? ''
          }`,
        )}
      </cr-flex>

      <markt-component-contact-manager
        .contacts=${this._selectedEntry.value.contacts ?? []}
        @record-edited=${async () => {
          this._data.value?.save();
          this.requestUpdate();
        }}
        @record-added=${async (e: ValueEvent<MarktAkquiseContactRecord>) => {
          this._selectedEntry.value?.contacts?.push(e.detail.value);
          await this._data.value?.save();
          this.requestUpdate();
        }}
        @record-deleted=${async (e: ValueEvent<MarktAkquiseContactRecord>) => {
          this._selectedEntry.value?.contacts?.splice(this._selectedEntry.value.contacts.indexOf(e.detail.value), 1);
          await this._data.value?.save();
          this.requestUpdate();
        }}
      ></markt-component-contact-manager>
    </cr-card>`;
  }

  private _dataviewCard() {
    return guard(
      [this._data.value],
      () =>
        html`<cr-card space="md" padding="md">
          <chancen-component-card-title>Veranstaltungen</chancen-component-card-title>
          ${this._dataView()}
        </cr-card>`,
    );
  }

  private _entryCard() {
    return this._entry();
  }

  override content() {
    return html` <cr-flex padding="sm" space="md"> ${this._dataviewCard()} ${this._entryCard()} </cr-flex> `;
  }
}
